import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import aiAssistant from '../../services/aiAssistant';

const AIAssistantChat = () => {
  const { isSuperAdmin, hasActiveSubscription } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Initialize AI assistant
    aiAssistant.setExamMode(false);
    setIsAvailable(aiAssistant.isAvailable());
    setSuggestions(aiAssistant.getSuggestions());
    
    // Add welcome message
    setMessages([
      { id: 1, text: "Hello! I'm your AI tutor for Zed Scholar! I can help you with various subjects and topics. What would you like to learn about today?", sender: 'ai', timestamp: new Date() }
    ]);
  }, []);

  useEffect(() => {
    // Check if we're in exam mode (would be passed from exam component)
    const checkExamMode = () => {
      const isExamMode = document.body.classList.contains('exam-mode') || 
                       document.querySelector('.exam-taking') !== null;
      aiAssistant.setExamMode(isExamMode);
      setIsAvailable(aiAssistant.isAvailable());
    };

    // Check periodically for exam mode changes
    const interval = setInterval(checkExamMode, 1000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!currentMessage.trim() || !isAvailable) return;

    const userMessage = {
      id: Date.now(),
      text: currentMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setIsTyping(true);

    try {
      // AI service now automatically includes conversation context
      const response = await aiAssistant.generateResponse(currentMessage);
      
      const aiMessage = {
        id: Date.now() + 1,
        text: response,
        sender: 'ai',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 2,
        text: 'Sorry, I encountered an error. Please try again.',
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setCurrentMessage(suggestion);
    setIsOpen(true);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Simple markdown parser for basic formatting
  const parseMarkdown = (text) => {
    // Convert **bold** to <strong>
    text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Convert *italic* to <em>
    text = text.replace(/\*(.*?)\*/g, '<em>$1</em>');
    // Convert line breaks to <br>
    text = text.replace(/\n/g, '<br>');
    return text;
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!hasActiveSubscription() && !isSuperAdmin()) {
    return (
      <div className="ai-assistant-disabled">
        <div className="disabled-message">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <span>AI Assistant requires an active subscription</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-assistant-container">
      {/* Floating Action Button */}
      {!isOpen && (
        <button
          className="ai-assistant-button"
          onClick={() => setIsOpen(true)}
          disabled={!isAvailable}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <span className="label">AI Help</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`ai-assistant-chat ${isFullscreen ? 'fullscreen' : ''}`}>
          <div className="chat-header">
            <div className="header-info">
              <span className="title">AI Assistant</span>
              <span className="status">
                {isAvailable ? '🟢 Online' : '🔴 Offline'}
              </span>
            </div>
            <div className="header-actions">
              <button 
                className="fullscreen-button"
                onClick={toggleFullscreen}
                title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? '⊟' : '⛶'}
              </button>
              <button 
                className="close-button"
                onClick={() => setIsOpen(false)}
              >
                ×
              </button>
            </div>
          </div>

          <div className="chat-messages">
            {messages.map((message) => (
              <div 
                key={message.id} 
                className={`message ${message.sender === 'user' ? 'user' : 'ai'}`}
              >
                <div className="message-content">
                  {message.sender === 'ai' ? (
                    <span dangerouslySetInnerHTML={{ __html: parseMarkdown(message.text) }} />
                  ) : (
                    message.text
                  )}
                </div>
                <span className="timestamp">{formatTime(message.timestamp)}</span>
              </div>
            ))}
            
            {isTyping && (
              <div className="message ai typing">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggestions */}
          {messages.length <= 1 && (
            <div className="chat-suggestions">
              <p className="suggestions-title">Try asking:</p>
              <div className="suggestions-grid">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    className="suggestion-btn"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="chat-input">
            <div className="input-container">
              <input
                type="text"
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                placeholder={isAvailable ? "Ask me anything about your studies..." : "AI Assistant unavailable"}
                disabled={!isAvailable}
              />
              <button
                onClick={handleSendMessage}
                disabled={!currentMessage.trim() || !isAvailable}
                className="send-button"
              >
                <span className="icon">➤</span>
              </button>
            </div>
            
            {!isAvailable && (
              <div className="unavailable-message">
                AI Assistant is currently unavailable
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssistantChat;
